import React, { useState, useEffect } from 'react';
import api from '../config/axiosConfig';
import { toast } from 'react-toastify';
import { getPlanLabel } from '../utils/traslations';
import { useNavigate } from 'react-router-dom';
import { 
    FaEdit, FaTrash, FaUserPlus, FaSearch, FaBuilding, 
    FaCreditCard, FaUserShield, FaEye, FaCheckCircle, FaTimesCircle, FaEnvelope, FaIdBadge,
    FaBan, FaChevronDown, FaTicketAlt, FaWrench, FaCalculator, FaChartBar
} from 'react-icons/fa';
import { PERMISSION_GROUPS, migrateOldPermissions, DEFAULT_AGENT_PERMISSIONS } from '../utils/permissions';
import HelpTooltip from '../components/Common/HelpTooltip';
import {
    IVA_OPTIONS,
    documentFieldLabel,
    documentKindForIva,
    normalizeIvaCondition,
    validateClientFiscalDocument,
} from '../utils/clientFiscalDocument';

interface User {
    id: number;
    username: string;
    full_name?: string;
    email: string;
    role: 'admin' | 'supervisor' | 'agent' | 'client' | 'viewer';
    status: 'active' | 'inactive';
    company_name?: string;
    department_name?: string;
    company_id?: number;
    department_id?: number;
    plan?: string;
    phone?: string | null;
    cuit?: string;
    iva_condition?: string | null;
    permissions?: string[];
}

interface Company {
    id: number;
    name: string;
}

interface Department {
    id: number;
    name: string;
}

const AdminUsersPage: React.FC = () => {
    const navigate = useNavigate();
    
    // Datos
    const [users, setUsers] = useState<User[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modales
    const [isModalOpen, setIsModalOpen] = useState(false); // Crear/Editar
    const [viewUser, setViewUser] = useState<User | null>(null); // ✅ NUEVO: Para ver tarjeta de detalle
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);

    // Formulario
    const [formData, setFormData] = useState<{
        username: string; full_name: string; email: string; password: string; cuit: string; iva_condition: string; phone: string;
        role: 'admin' | 'supervisor' | 'agent' | 'client' | 'viewer';
        status: 'active' | 'inactive';
        company_id: string; department_id: string;
        permissions: string[];
        can_manage_tech_finances: boolean;
    }>({
        username: '',
        full_name: '',
        email: '',
        password: '',
        cuit: '',
        iva_condition: '',
        phone: '',
        role: 'client',
        status: 'active',
        company_id: '',
        department_id: '',
        permissions: DEFAULT_AGENT_PERMISSIONS,
        can_manage_tech_finances: false,
    });

    const fetchData = async () => {
        try {
            const [usersRes, companiesRes, deptsRes] = await Promise.all([
                api.get('/api/users'),
                api.get('/api/companies'),
                api.get('/api/departments')
            ]);

            setUsers(Array.isArray(usersRes.data.data) ? usersRes.data.data : []);
            setCompanies(Array.isArray(companiesRes.data.data) ? companiesRes.data.data : []);
            setDepartments(Array.isArray(deptsRes.data.data) ? deptsRes.data.data : []);

        } catch (error) {
            console.error("Error cargando datos:", error);
            toast.error('Error al cargar datos del sistema');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- MANEJADORES ---

    const handleManagePlan = (userId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // Evita abrir el modal de ver detalle
        navigate(`/admin/users/${userId}/payments`);
    };

    const handleOpenCreate = () => {
        setFormData({ username: '', full_name: '', email: '', password: '', cuit: '', iva_condition: '', phone: '', role: 'client', status: 'active', company_id: '', department_id: '', permissions: DEFAULT_AGENT_PERMISSIONS, can_manage_tech_finances: false });
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (user: User, e?: React.MouseEvent) => {
        if(e) e.stopPropagation();
        const raw = Array.isArray(user.permissions) ? user.permissions : [];
        const perms = raw.length > 0 ? migrateOldPermissions(raw) : (user.role === 'agent' || user.role === 'supervisor' ? DEFAULT_AGENT_PERMISSIONS : []);
        const u = user as User & { cuit?: string; iva_condition?: string | null; phone?: string | null; can_manage_tech_finances?: boolean };
        setFormData({
            username: user.username,
            full_name: user.full_name ?? user.username ?? '',
            email: user.email,
            password: '',
            cuit: u.cuit ?? '',
            iva_condition: normalizeIvaCondition(u.iva_condition) || '',
            phone: u.phone ?? '',
            role: user.role,
            status: user.status || 'active',
            company_id: user.company_id ? user.company_id.toString() : '',
            department_id: user.department_id ? user.department_id.toString() : '',
            permissions: perms,
            can_manage_tech_finances: Boolean(u.can_manage_tech_finances),
        });
        setCurrentUserId(user.id);
        setIsEditMode(true);
        setIsModalOpen(true);
    };

    const handleViewDetails = (user: User) => {
        setDeleteMenuUserId(null);
        setViewUser(user);
    };

    const [deleteMenuUserId, setDeleteMenuUserId] = useState<number | null>(null);

    const handleDeactivate = async (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setDeleteMenuUserId(null);
        if (!window.confirm('¿Desactivar este usuario? No podrá iniciar sesión.')) return;
        try {
            await api.delete(`/api/users/${id}`);
            toast.success('Usuario desactivado');
            fetchData();
        } catch (error) {
            toast.error('Error al desactivar');
        }
    };

    const handlePermanentDelete = async (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setDeleteMenuUserId(null);
        if (!window.confirm('¿Eliminar PERMANENTEMENTE este usuario? Esta acción no se puede deshacer. Solo es posible si no tiene tickets o comentarios.')) return;
        try {
            await api.delete(`/api/users/${id}?permanent=1`);
            toast.success('Usuario eliminado permanentemente');
            fetchData();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error al eliminar');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.username || !formData.email) return toast.warn('Completa los campos obligatorios');
        if (formData.role === 'client' && !formData.company_id) return toast.warn('Asigna una empresa al cliente');
        if (formData.role === 'client') {
            const fiscal = validateClientFiscalDocument({
                iva_condition: formData.iva_condition,
                cuit: formData.cuit,
            });
            if (!fiscal.ok) return toast.warn(fiscal.message);
        }

        try {
            const payload: Record<string, unknown> = {
                ...formData,
                full_name: (formData.full_name || '').trim() || null,
                iva_condition: formData.role === 'client' ? (normalizeIvaCondition(formData.iva_condition) || null) : (formData.iva_condition || null),
                company_id: formData.company_id ? parseInt(formData.company_id) : null,
                department_id: formData.department_id ? parseInt(formData.department_id) : null,
                permissions: (formData.role === 'agent' || formData.role === 'supervisor' || formData.role === 'viewer') ? formData.permissions : undefined,
                can_manage_tech_finances: formData.role === 'agent' ? formData.can_manage_tech_finances : false,
            };
            if (payload.permissions === undefined) delete payload.permissions;
            if (!isEditMode) payload.accepted_confidentiality_agreement = true;

            if (isEditMode && currentUserId) {
                await api.put(`/api/users/${currentUserId}`, payload);
                toast.success('Usuario actualizado');
            } else {
                await api.post('/api/auth/register', payload);
                toast.success('Usuario creado');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error: any) {
            const msg = error.response?.data?.message || error.message || 'Error al guardar';
            toast.error(msg, { autoClose: 8000 });
        }
    };

    const safeUsers = Array.isArray(users) ? users : [];
    const filteredUsers = safeUsers.filter(u => 
        (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-8 text-center text-gray-500">Cargando gestión de usuarios...</div>;

    return (
        <div className="p-6 min-h-screen bg-gray-50">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <FaUserShield className="text-indigo-600"/> Gestión de Usuarios
                    </h1>
                    <p className="text-gray-500 mt-1">Administra accesos, roles y estados.</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:w-72">
                        <FaSearch className="absolute left-3 top-3 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Buscar por nombre o email..." 
                            className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button onClick={handleOpenCreate} className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 transition font-bold shadow-md flex items-center justify-center gap-2">
                        <FaUserPlus /> Nuevo Usuario
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="p-5 font-bold">Usuario</th>
                                <th className="p-5 font-bold">Nombre y apellido</th>
                                <th className="p-5 font-bold">Estado & Rol</th>
                                <th className="p-5 font-bold">Empresa</th>
                                <th className="p-5 font-bold text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map(user => (
                                    <tr 
                                        key={user.id} 
                                        className="hover:bg-indigo-50/40 transition duration-150 cursor-pointer"
                                        onClick={() => handleViewDetails(user)} // ✅ CLIC EN LA FILA ABRE EL DETALLE
                                    >
                                        <td className="p-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                                    user.role === 'admin' ? 'bg-red-500' : user.role === 'supervisor' ? 'bg-amber-500' : user.role === 'agent' ? 'bg-purple-500' : user.role === 'viewer' ? 'bg-gray-500' : 'bg-blue-500'
                                                }`}>
                                                    {(user.username || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-gray-800">{user.username}</div>
                                                    <div className="text-xs text-gray-500">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            <span className="text-gray-800 font-medium">{(user.full_name || user.username || '—').trim() || '—'}</span>
                                        </td>
                                        <td className="p-5">
                                            <div className="flex flex-col gap-1 items-start">
                                                {/* Badge de Estado */}
                                                <span className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                                                    user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                                                }`}>
                                                    {user.status === 'active' ? <FaCheckCircle/> : <FaTimesCircle/>}
                                                    {user.status === 'active' ? 'Activo' : 'Inactivo'}
                                                </span>
                                                
                                                {/* Badge de Rol */}
                                                <span className="text-xs font-semibold uppercase text-gray-500 tracking-wide mt-1">
                                                    {user.role}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="p-5">
                                            {user.role === 'client' ? (
                                                <div className="text-sm">
                                                    <div className="text-gray-800 font-medium flex items-center gap-2">
                                                        <FaBuilding className="text-gray-400"/> 
                                                        {user.company_name || 'Sin Empresa Asignada'}
                                                    </div>
                                                </div>
                                            ) : user.role === 'agent' || user.role === 'supervisor' || user.role === 'admin' || user.role === 'viewer' ? (
                                                <span className="text-gray-400 text-sm italic">Interno (CASA SCHETTINI)</span>
                                            ) : null}
                                        </td>

                                        <td className="p-5">
                                            <div className="flex items-center justify-center gap-2">
                                                {/* Botón Ver Detalle (Ojo) */}
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); handleViewDetails(user); }}
                                                    className="bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 transition border border-gray-200"
                                                    title="Ver Ficha Completa"
                                                >
                                                    <FaEye />
                                                </button>

                                                {user.role === 'client' && (
                                                    <button 
                                                        onClick={(e) => handleManagePlan(user.id, e)}
                                                        className="bg-yellow-50 text-yellow-600 p-2 rounded-lg hover:bg-yellow-100 transition border border-yellow-200"
                                                        title="Pagos y Suscripción"
                                                    >
                                                        <FaCreditCard />
                                                    </button>
                                                )}

                                                <button 
                                                    onClick={(e) => handleOpenEdit(user, e)} 
                                                    className="bg-blue-50 text-blue-600 p-2 rounded-lg hover:bg-blue-100 transition border border-blue-200" 
                                                    title="Editar"
                                                >
                                                    <FaEdit />
                                                </button>

                                                <div className="relative">
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); setDeleteMenuUserId(deleteMenuUserId === user.id ? null : user.id); }} 
                                                        className="bg-red-50 text-red-600 p-2 rounded-lg hover:bg-red-100 transition border border-red-200 flex items-center gap-1" 
                                                        title="Eliminar / Desactivar"
                                                    >
                                                        <FaTrash /><FaChevronDown size={10} />
                                                    </button>
                                                    {deleteMenuUserId === user.id && (
                                                        <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                                            <button onClick={(e) => handleDeactivate(user.id, e)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-amber-600">
                                                                <FaBan /> Desactivar
                                                            </button>
                                                            <button onClick={(e) => handlePermanentDelete(user.id, e)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600">
                                                                <FaTrash /> Eliminar permanentemente
                                                            </button>
                                                            <button onClick={(e) => { e.stopPropagation(); setDeleteMenuUserId(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-500">
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-10 text-center">
                                        <div className="text-gray-600 font-medium mb-1">Aún no hay registros aquí.</div>
                                        <div className="text-gray-500 text-sm">Hacé clic en el botón «Nuevo Usuario» arriba a la derecha para comenzar.</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ✅ MODAL DE TARJETA DE USUARIO (POP-UP DE DETALLE) */}
            {viewUser && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative">
                        {/* Header del Modal */}
                        <div className={`h-24 w-full flex items-center justify-center ${
                            viewUser.role === 'admin' ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                            viewUser.role === 'supervisor' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                            viewUser.role === 'agent' ? 'bg-gradient-to-r from-purple-500 to-indigo-500' :
                            viewUser.role === 'viewer' ? 'bg-gradient-to-r from-gray-500 to-slate-600' :
                            'bg-gradient-to-r from-blue-500 to-cyan-500'
                        }`}>
                            <button onClick={() => setViewUser(null)} className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold">&times;</button>
                        </div>

                        {/* Avatar y Datos Principales */}
                        <div className="px-8 pb-8 -mt-12 text-center">
                            <div className="bg-white p-2 rounded-full inline-block shadow-lg">
                                <div className={`w-24 h-24 rounded-full flex items-center justify-center text-4xl font-bold text-white ${
                                    viewUser.role === 'admin' ? 'bg-red-500' : viewUser.role === 'supervisor' ? 'bg-amber-500' : viewUser.role === 'agent' ? 'bg-purple-500' : viewUser.role === 'viewer' ? 'bg-gray-500' : 'bg-blue-500'
                                }`}>
                                    {(viewUser.full_name || viewUser.username || '?').charAt(0).toUpperCase()}
                                </div>
                            </div>
                            
                            <h2 className="text-2xl font-bold text-gray-800 mt-3">{((viewUser.full_name || '').trim() || viewUser.username || '').trim() || '—'}</h2>
                            <p className="text-sm text-gray-500 mt-0.5">Usuario: {viewUser.username}</p>
                            <p className="text-gray-500 flex items-center justify-center gap-2 mt-2">
                                <FaEnvelope className="text-gray-400"/> {viewUser.email}
                            </p>

                            <div className="flex justify-center gap-3 mt-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                    viewUser.role === 'admin' ? 'bg-red-100 text-red-700' :
                                    viewUser.role === 'supervisor' ? 'bg-amber-100 text-amber-700' :
                                    viewUser.role === 'agent' ? 'bg-purple-100 text-purple-700' :
                                    viewUser.role === 'viewer' ? 'bg-gray-100 text-gray-700' :
                                    'bg-blue-100 text-blue-700'
                                }`}>
                                    {viewUser.role}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${
                                    viewUser.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                    {viewUser.status === 'active' ? <FaCheckCircle/> : <FaTimesCircle/>} {viewUser.status || 'Desconocido'}
                                </span>
                            </div>

                            {/* Detalles Técnicos */}
                            <div className="mt-8 grid grid-cols-2 gap-4 text-left bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <div>
                                    <p className="text-xs text-gray-400 uppercase font-bold">Organización</p>
                                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-2 mt-1">
                                        <FaBuilding className="text-gray-400"/>
                                        {viewUser.role === 'client' ? (viewUser.company_name || 'Sin asignar') : 'Interno CASA SCHETTINI'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-400 uppercase font-bold">Departamento</p>
                                    <p className="text-sm font-semibold text-gray-700 mt-1">
                                        {viewUser.department_name || 'Sin asignar'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-400 uppercase font-bold">ID Sistema</p>
                                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-2 mt-1">
                                        <FaIdBadge className="text-gray-400"/> #{viewUser.id}
                                    </p>
                                </div>
                                {viewUser.role === 'client' && (
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase font-bold">Plan Actual</p>
                                        <p className="text-sm font-semibold text-indigo-600 mt-1">
                                            {getPlanLabel(viewUser.plan)}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <button 
                                onClick={() => setViewUser(null)}
                                className="mt-6 w-full bg-gray-800 text-white py-3 rounded-lg font-bold hover:bg-gray-900 transition shadow-lg"
                            >
                                Cerrar Ficha
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL CREAR / EDITAR */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl relative flex flex-col max-h-[92vh]">
                        {/* HEADER FIJO — título y cerrar siempre visibles */}
                        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-gray-200 flex-shrink-0">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Completá los datos del usuario. Los campos con <span className="text-red-500">*</span> son obligatorios. Pasá el mouse o tocá el ícono de ayuda (?) junto a cada etiqueta para más información.
                                </p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="flex-shrink-0 text-gray-400 hover:text-gray-600 text-3xl leading-none font-bold -mt-1"
                                aria-label="Cerrar"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
                            {/* BODY SCROLLEABLE — los campos scrollean aquí, no todo el modal */}
                            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    Usuario <span className="text-red-500">*</span>
                                    <HelpTooltip text="Nombre de usuario para iniciar sesión. Sin espacios, por ejemplo: jperez o nombre.empresa. Debe ser único en el sistema." />
                                </label>
                                <input 
                                    type="text" required placeholder="Usuario para inicio de sesión (ej. agusortega)"
                                    className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                    value={formData.username}
                                    onChange={e => setFormData({...formData, username: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    Nombre y apellido
                                    <HelpTooltip text="Nombre completo de la persona o Razón Social si es empresa." />
                                </label>
                                <input 
                                    type="text" placeholder="Ej. Agustín Ortega o Razón Social"
                                    className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                    value={formData.full_name}
                                    onChange={e => setFormData({...formData, full_name: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    Correo Electrónico <span className="text-red-500">*</span>
                                    <HelpTooltip text="Correo con el que el usuario recibirá notificaciones y podrá recuperar su contraseña. Debe ser único." />
                                </label>
                                <input 
                                    type="email" required
                                    className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                    value={formData.email}
                                    onChange={e => setFormData({...formData, email: e.target.value})}
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    Teléfono / WhatsApp
                                    <HelpTooltip text="Número para contacto y envío de WhatsApp desde las órdenes." />
                                </label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                    placeholder="Ej: 11 1234-5678 o +54 9 11 1234-5678"
                                    value={formData.phone}
                                    onChange={e => setFormData({...formData, phone: e.target.value})}
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    {isEditMode ? 'Nueva Contraseña (Opcional)' : 'Contraseña'}
                                    <HelpTooltip text={isEditMode ? 'Dejá en blanco para no cambiar la contraseña actual. Si completás, se actualizará al guardar.' : 'La contraseña debe ser segura. El usuario la usará para entrar al sistema.'} />
                                </label>
                                <input 
                                    type="password" 
                                    required={!isEditMode}
                                    className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                    value={formData.password}
                                    onChange={e => setFormData({...formData, password: e.target.value})}
                                    placeholder={isEditMode ? "Dejar en blanco para mantener actual" : "••••••"}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                        Rol
                                        <HelpTooltip text="Cliente: acceso al portal de clientes. Agente/Supervisor: área técnica y tickets. Vista: solo lectura. Admin: acceso total al sistema." />
                                    </label>
                                    <select 
                                        className="w-full border border-gray-300 p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={formData.role}
                                        onChange={e => {
                                            const newRole = e.target.value as 'admin'|'supervisor'|'agent'|'client'|'viewer';
                                            const perms = (newRole === 'agent' || newRole === 'supervisor' || newRole === 'viewer') && (!formData.permissions?.length)
                                                ? DEFAULT_AGENT_PERMISSIONS : (formData.permissions?.length ? formData.permissions : []);
                                            setFormData({...formData, role: newRole, permissions: perms});
                                        }}
                                    >
                                        <option value="client">Cliente</option>
                                        <option value="agent">Agente</option>
                                        <option value="supervisor">Supervisor</option>
                                        <option value="viewer">Vista (solo lectura)</option>
                                        <option value="admin">Administrador</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                        Estado
                                        <HelpTooltip text="Inactivo: el usuario no podrá iniciar sesión hasta que lo vuelvas a activar." />
                                    </label>
                                    <select 
                                        className="w-full border border-gray-300 p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={formData.status}
                                        onChange={e => setFormData({...formData, status: e.target.value as any})}
                                    >
                                        <option value="active">Activo</option>
                                        <option value="inactive">Inactivo</option>
                                    </select>
                                </div>
                            </div>

                            {formData.role === 'client' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                        Condición IVA <span className="text-red-500">*</span>
                                        <HelpTooltip text="Obligatorio para clientes. Define si pedimos CUIT (Inscripto/Monotributista) o DNI (Consumidor Final/Exento)." />
                                    </label>
                                    <select
                                        required
                                        className="w-full border border-gray-300 p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={formData.iva_condition}
                                        onChange={e => setFormData({ ...formData, iva_condition: e.target.value })}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {IVA_OPTIONS.map((o) => (
                                            <option key={o} value={o}>{o}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                        {documentFieldLabel(formData.iva_condition)} <span className="text-red-500">*</span>
                                        <HelpTooltip
                                          text={
                                            documentKindForIva(formData.iva_condition) === 'dni'
                                              ? 'DNI de 7 u 8 dígitos.'
                                              : documentKindForIva(formData.iva_condition) === 'cuit'
                                                ? 'CUIT de 11 dígitos (con o sin guiones).'
                                                : 'Elegí primero la condición IVA.'
                                          }
                                        />
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder={documentKindForIva(formData.iva_condition) === 'dni' ? '12345678' : '20-12345678-9'}
                                        className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                        value={formData.cuit}
                                        onChange={e => setFormData({...formData, cuit: e.target.value})}
                                    />
                                </div>
                            </div>
                            )}

                            {(formData.role === 'agent' || formData.role === 'viewer') && (
                                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                    <input
                                        type="checkbox"
                                        id="can_manage_tech_finances"
                                        checked={formData.can_manage_tech_finances}
                                        onChange={e => setFormData({...formData, can_manage_tech_finances: e.target.checked})}
                                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label htmlFor="can_manage_tech_finances" className="text-sm font-medium text-gray-800 cursor-pointer">
                                        Permitir acceso a Finanzas Técnicas (Caja Técnica y Reportes de Deudas)
                                    </label>
                                </div>
                            )}

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    Empresa {formData.role === 'client' && <span className="text-red-500">*</span>}
                                    <HelpTooltip text="Solo para clientes. Asigná la empresa a la que pertenece el usuario. Los roles internos (agente, admin, etc.) no usan este campo." />
                                </label>
                                <select 
                                    className={`w-full border p-2.5 rounded-lg bg-white outline-none transition ${formData.role === 'client' ? 'border-gray-300 focus:ring-2 focus:ring-indigo-500' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                                    value={formData.company_id}
                                    onChange={e => setFormData({...formData, company_id: e.target.value})}
                                    required={formData.role === 'client'}
                                    disabled={formData.role !== 'client'}
                                >
                                    <option value="">Seleccionar...</option>
                                    {Array.isArray(companies) && companies.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    Departamento (Opcional)
                                    <HelpTooltip text="Si la empresa tiene departamentos, asigná uno. Sirve para filtrar y organizar usuarios por área." />
                                </label>
                                <select 
                                    className="w-full border border-gray-300 p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                    value={formData.department_id}
                                    onChange={e => setFormData({...formData, department_id: e.target.value})}
                                >
                                    <option value="">Ninguno</option>
                                    {Array.isArray(departments) && departments.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </div>

                            {(formData.role === 'agent' || formData.role === 'supervisor' || formData.role === 'viewer') && (
                                <div className="border-t pt-4 mt-2">
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                                        Permisos de Acceso (Agentes, Supervisores y Vista)
                                        <HelpTooltip text="Marcá qué secciones puede ver y usar este usuario. Podés ser muy específico: solo planillas, solo movimientos, solo reportes, etc. El admin tiene todo por defecto." />
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {PERMISSION_GROUPS.map((group) => (
                                            <div key={group.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 hover:bg-gray-50 transition">
                                                <div className="flex items-center gap-2 mb-3 font-semibold text-gray-800">
                                                    <span className="text-lg">{group.icon}</span>
                                                    <span>{group.title}</span>
                                                </div>
                                                <div className="space-y-2">
                                                    {group.perms.map(({ key, label }) => (
                                                        <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.permissions.includes(key)}
                                                                onChange={e => {
                                                                    if (e.target.checked) {
                                                                        setFormData({ ...formData, permissions: [...formData.permissions, key] });
                                                                    } else {
                                                                        setFormData({ ...formData, permissions: formData.permissions.filter(p => p !== key) });
                                                                    }
                                                                }}
                                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                            />
                                                            <span className="text-gray-700">{label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            </div>
                            {/* FOOTER FIJO — botones siempre visibles */}
                            <div className="flex gap-4 px-6 py-4 border-t border-gray-200 bg-gray-50/60 rounded-b-xl flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 bg-white border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition font-bold"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition font-bold shadow-lg"
                                >
                                    {isEditMode ? 'Guardar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsersPage;